import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AppContext";
import { api, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({ total_products: 0, total_customers: 0 });
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) nav("/login");
    if (user?.role === "admin") load();
  }, [user, ready]);

  const load = async () => {
    try {
      const [{ data: s }, { data: l }, { data: p }, { data: c }] = await Promise.all([
        api.get("/admin/stats").catch(() => ({ data: {} })),
        api.get("/admin/leads"),
        api.get("/products"),
        api.get("/admin/contacts"),
      ]);
      setStats(s || {}); setLeads(l); setProducts(p); setContacts(c);
    } catch { toast.error("Failed to load admin data"); }
  };

  const updateLead = async (id, status) => {
    try { await api.put(`/admin/leads/${id}/status`, { status }); toast.success("Lead updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  if (!user) return <div className="py-24 text-center text-asure-text2">Loading…</div>;

  const totalLeads = leads.length;
  const newLeads = leads.filter((l) => l.status === "new").length;
  const contactedLeads = leads.filter((l) => l.status === "contacted").length;
  const convertedLeads = leads.filter((l) => l.status === "converted").length;

  return (
    <div className="min-h-screen bg-asure-ivory">
      <header className="bg-white border-b border-[color:var(--asure-border)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="" className="h-8" /><span className="font-display text-2xl">Admin</span></div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-asure-text2 hover:text-asure-forest">← Storefront</Link>
            <button onClick={logout} className="text-sm text-asure-text2 hover:text-asure-orange" data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1">
          {[["dashboard","Dashboard"],["leads","Leads"],["products","Products"],["contacts","Contact Messages"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${tab === k ? 'bg-asure-forest text-white' : 'hover:bg-white'}`}>{l}</button>
          ))}
        </aside>
        <main>
          {tab === "dashboard" && (
            <div className="grid md:grid-cols-4 gap-4">
              <Stat l="Total Leads" v={totalLeads} accent />
              <Stat l="New" v={newLeads} />
              <Stat l="Contacted" v={contactedLeads} />
              <Stat l="Converted" v={convertedLeads} />
              <Stat l="Products" v={stats.total_products || products.length} />
              <Stat l="Contact Messages" v={contacts.length} />
            </div>
          )}
          {tab === "leads" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-asure-text2">{leads.length} lead(s) total</div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv`}
                  onClick={(e) => {
                    e.preventDefault();
                    const token = localStorage.getItem("asure_token");
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv`, { headers: { Authorization: `Bearer ${token}` } })
                      .then((r) => r.blob()).then((b) => {
                        const url = URL.createObjectURL(b);
                        const a = document.createElement("a"); a.href = url; a.download = `asure_leads_${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
                      });
                  }}
                  className="btn-ghost !py-2 !px-4 text-sm" data-testid="export-csv">Export CSV</a>
              </div>
              <div className="card-surface p-6 overflow-x-auto">
              {leads.length === 0 ? <div className="text-asure-text2 text-center py-10">No leads yet.</div> :
              <table className="w-full text-sm">
                <thead><tr className="text-left text-asure-text2 border-b border-[color:var(--asure-border)]">
                  <th className="py-3">Received</th><th>Name</th><th>Mobile</th><th>City</th><th>Product</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-[color:var(--asure-border)]" data-testid={`lead-row-${l.id}`}>
                      <td className="py-3 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                      <td>
                        <div className="font-semibold">{l.full_name}</div>
                        {l.email && <div className="text-xs text-asure-text2">{l.email}</div>}
                      </td>
                      <td><a href={`tel:${l.mobile}`} className="text-asure-forest hover:underline">{l.mobile}</a></td>
                      <td>{l.city}</td>
                      <td className="max-w-[220px]"><div className="text-xs">{l.product_interest}</div></td>
                      <td>
                        <select value={l.status} onChange={(e) => updateLead(l.id, e.target.value)} className="text-xs border rounded px-2 py-1" data-testid={`lead-status-${l.id}`}>
                          {["new","contacted","converted","not_interested"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
              </div>
            </div>
          )}
          {tab === "products" && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div className="text-sm text-asure-text2">{products.length} product(s)</div>
                <button
                  onClick={async () => {
                    if (!window.confirm("Wipe and reseed the product catalog from the latest code? This will delete existing products, categories, ingredients and journal — users, leads and orders are safe.")) return;
                    try {
                      const { data } = await api.post("/admin/reseed-catalog");
                      toast.success(`Catalog reseeded: ${data.products} products`);
                      load();
                    } catch { toast.error("Reseed failed"); }
                  }}
                  data-testid="admin-reseed"
                  className="btn-ghost !py-2 !px-4 text-sm"
                >Reseed Catalog</button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {products.map((p) => (
                  <div key={p.id} className="card-surface p-4 flex gap-4">
                    <img src={p.images?.[0]} alt="" className="w-24 h-24 object-cover rounded" />
                    <div className="flex-1">
                      <div className="font-display text-lg">{p.name}</div>
                      <div className="text-xs text-asure-text2 capitalize">{p.category.replace(/-/g, ' ')}</div>
                      <div className="mt-2 text-sm">₹{p.price} · MRP ₹{p.mrp}</div>
                      <div className="mt-1 flex gap-1">
                        {p.is_hero && <span className="text-[10px] font-bold px-2 py-0.5 bg-asure-forest text-white rounded-full">HERO</span>}
                        {p.featured && !p.is_hero && <span className="text-[10px] font-bold px-2 py-0.5 bg-asure-orange text-white rounded-full">FEATURED</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "contacts" && (
            <div className="space-y-3">
              {contacts.length === 0 ? <div className="text-asure-text2">No contact messages yet.</div> :
                contacts.map((c) => (
                  <div key={c.id} className="card-surface p-5">
                    <div className="flex justify-between"><div className="font-semibold">{c.name}</div><div className="text-xs text-asure-text2">{new Date(c.created_at).toLocaleString()}</div></div>
                    <div className="text-xs text-asure-text2">{c.email} · {c.subject}</div>
                    <div className="mt-2 text-sm">{c.message}</div>
                  </div>
                ))
              }
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
function Stat({ l, v, accent }) {
  return <div className={`card-surface p-6 ${accent ? 'bg-asure-forest text-white border-asure-forest' : ''}`}>
    <div className="eyebrow" style={{ color: accent ? "var(--asure-gold)" : undefined }}>{l}</div>
    <div className={`font-display text-4xl mt-2 ${accent ? '' : 'text-asure-forest'}`}>{v}</div>
  </div>;
}
